import { apiSlice } from '../apiSlice';
import { CUSTOM_LIVE_CHAT_ONLY_LOGIN_ERROR, MESSAGES } from './authMessages';
import { TEMP_STORAGE_KEYS, AUTHENTICATION_METHODS, ADDITIONAL_ROLES_ORG } from '../../constants/main';
import { authActions } from './authSlice';
import { logoutUser } from './authActions';
import { API_TAGS } from '../../constants/api';
import { isTokenExpired } from '../../utils/auth/isTokenExpired';
import { IS_LIVECHAT_CONSOLE_ONLY } from '../../constants/env';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

const getStoredUser = () => {
  const storedEntry = window.localStorage.getItem(TEMP_STORAGE_KEYS.USER);
  return storedEntry && JSON.parse(storedEntry);
};

const getAuthenticationMethod = (method) => {
  switch (method) {
    case AUTHENTICATION_METHODS.GOOGLE:
      return 'login/google/';
    case AUTHENTICATION_METHODS.MICROSOFT:
      return 'login/microsoft/';
    case AUTHENTICATION_METHODS.TEAMS:
      return 'login/teams/';
    case AUTHENTICATION_METHODS.INTEGRATION:
      return 'login/integration/';
    default:
      return 'login/';
  }
};

export const authSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    fetchMe: build.query({
      query: () => ({
        url: 'users/me',
        method: 'GET',
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_ME.ERROR,
          errorCodesToIgnore: [401]
        }
      }),
      transformResponse: (response) => {
        return {
          ...getStoredUser(),
          ...response
        };
      },
      onQueryStarted: async (_args, { dispatch, queryFulfilled, getState }) => {
        await dispatch(authActions.userFetchStart());

        await queryFulfilled.then(async response => {
          let userData = response?.data;

          if (IS_LIVECHAT_CONSOLE_ONLY && userData) {
            const liveChatRoles = Object.values(ADDITIONAL_ROLES_ORG);
            const { effectiveRoles, organizations } = userData;

            const allowedOrgIds = new Set();
            if (effectiveRoles?.organizationRoles) {
              for (const orgId in effectiveRoles.organizationRoles) {
                const roles = effectiveRoles.organizationRoles[orgId];
                if (roles.some(role => liveChatRoles.includes(role))) {
                  allowedOrgIds.add(orgId);
                }
              }
            }

            const filteredOrganizations = organizations.filter(org => allowedOrgIds.has(org.id));

            userData = {
              ...userData,
              organizations: filteredOrganizations
            };
          }

          await dispatch(authActions.userFetchComplete(userData));
        }).catch(async error => {
          await dispatch(authActions.userFetchFail(error?.error));
        });
      },
      providesTags: () => {
        return [{ type: API_TAGS.AUTH, id: 'Me' }];
      }
    }),

    logoutUser: build.mutation({
      query: () => ({
        url: 'logout',
        method: 'POST'
      })
    }),

    refreshToken: build.mutation({
      query: ({ refreshToken }) => ({
        url: 'login/refresh',
        method: 'POST',
        body: { token: refreshToken },
        withAuth: false,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.REFRESH_TOKEN.ERROR
        }
      }),
      onQueryStarted: async (_args, { dispatch, queryFulfilled, getState }) => {
        const isTokenInvalid = isTokenExpired(getState().auth.token);

        try {
          const response = await queryFulfilled;
          await dispatch(authActions.refreshTokenComplete(response?.data));
        } catch (error) {
          logoutUser();
          await dispatch(authActions.refreshTokenFail(error));
        }

        if (isTokenInvalid) {
          await dispatch(apiSlice.util.invalidateTags([...Object.values(API_TAGS)]));
        }
      }
    }),

    authenticateUser: build.mutation({
      query: ({ authenticationMethod, data, currentUrl, errorMessage }) => ({
        url: getAuthenticationMethod(authenticationMethod),
        method: 'POST',
        body: data,
        withAuth: false,
        errorMessage: errorMessage ?? {
          showMessage: true,
          messages: MESSAGES.LOGIN_USER.ERROR
        }
      }),

      transformResponse: (response, _meta, args) => {
        const { organizations } = response.user;
        const firstOrgAvailable = organizations[0]?.id;
        const availableUrlPaths = args.currentUrl?.split('/');
        const urlOrg = availableUrlPaths ? availableUrlPaths[availableUrlPaths?.indexOf('org') + 1] : null;
        return {
          ...response,
          redirectUrl: !!args?.currentUrl,
          redirectOrg: args?.currentUrl ? urlOrg : firstOrgAvailable
        };
      },
      onQueryStarted: async (args, { dispatch, queryFulfilled, getState }) => {
        await queryFulfilled.then(async response => {
          let responseData = response.data;

          // If LIVECHAT_CONSOLE is enabled then we want to throw an error message when trying to connect a user without LiveChat Access to any org
          if (IS_LIVECHAT_CONSOLE_ONLY && responseData) {
            const { effectiveRoles, organizations } = responseData.user;
            const liveChatRoles = Object.values(ADDITIONAL_ROLES_ORG);

            const allowedOrgIds = new Set();
            if (effectiveRoles?.organizationRoles) {
              for (const orgId in effectiveRoles.organizationRoles) {
                const roles = effectiveRoles.organizationRoles[orgId];
                if (roles.some(role => liveChatRoles.includes(role))) {
                  allowedOrgIds.add(orgId);
                }
              }
            }

            const filteredOrganizations = organizations.filter(org => allowedOrgIds.has(org.id));

            if (filteredOrganizations.length === 0) {
              throw CUSTOM_LIVE_CHAT_ONLY_LOGIN_ERROR;
            }

            const updatedUser = { ...responseData.user, organizations: filteredOrganizations };
            const firstOrgAvailable = filteredOrganizations[0]?.id;
            const availableUrlPaths = args.currentUrl?.split('/');
            const urlOrg = availableUrlPaths ? availableUrlPaths[availableUrlPaths?.indexOf('org') + 1] : null;

            responseData = {
              ...responseData,
              user: updatedUser,
              redirectOrg: args?.currentUrl ? urlOrg : firstOrgAvailable
            };
          }

          await dispatch(authActions.organizationChangeComplete(responseData.redirectOrg));
          await dispatch(authActions.userLoginComplete(responseData));
        }).catch(async error => {
          if(error.error?.code === 'HBF-AUTH-LIVECHAT-0006') {
            openConsoleAlertMessage({ type: 'error', message: error.error.description });
          }
          await dispatch(authActions.userLoginFail(error));
        });
      }
    }),

    verifyEmail: build.mutation({
      query: ({ token }) => ({
        url: `public/email-verification`,
        method: 'POST',
        body: { token },
        withAuth: false,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.VERIFY_EMAIL.ERROR
        }
      }),
      transformResponse: () => true
    })
  })
});

export const { useLazyFetchMeQuery, useVerifyEmailMutation, useRefreshTokenMutation, useAuthenticateUserMutation, useLogoutUserMutation } = authSlice;
